import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import "./people.css";
import "./team-timeoff.css";
import { CATEGORIES, listTimeOff, createTimeOff, approveTimeOff, denyTimeOff, cancelTimeOff, getTimeOffConflicts, timeOffErrorMessage } from "../../api/timeOffApi";
import { listEmployees, listTechnicianProfiles, techUserId } from "../../api/teamApi";
import { getUserRole } from "../../helpers/tenantHelpers";

/* Team → Time off: MANAGEMENT, not a form (founder, 2026-09-08).
   - Who is off, when: a calendar (this week / this month) of approved and
     pending time off across people.
   - How much each person has left: balance from the employment record.
   - Pending queue with conflict detection ("Grant has 12 stops that day") from
     CRM schedule rows, approve/deny in place. Approval writes the availability
     block, so an approved day off blocks the tech on the dispatch board.
   - Request on behalf: managers name the employee (backend accepts `tech` for
     managers); the form is one button on the surface.
   - Caps, blackout dates, accrual: no backend policy exists yet — said plainly,
     spec with lane D (docs/TEAM_TIME_OFF_ASKS_2026-09-08.md).
   - HC 2026-10-22: a past-dated request is approvable (it happened; it still
     counts) — badged "Past", never a dead button — with a one-line note kept on
     the request, and who/when shown under "Decided". Balances read the
     per-employee allowance (days); none set = "PTO not set — set it", never 0. */

const MANAGER_ROLES = new Set(["owner", "admin", "manager"]);
const DAY = 86400000;
const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const parse = (s) => new Date(String(s).slice(0, 10) + "T00:00:00");
const fmtDate = (v) => (v ? parse(v).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
const startOfWeek = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); x.setHours(0, 0, 0, 0); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const daysBetween = (a, b) => Math.round((parse(b) - parse(a)) / DAY) + 1;

export default function TeamTimeOff({ openEmployee }) {
  const role = String(getUserRole("Member") || "").toLowerCase();
  const isManager = MANAGER_ROLES.has(role);
  const [rows, setRows] = useState([]);
  const [techs, setTechs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [notes, setNotes] = useState({}); // request id → one-line approve/deny note
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(() => new Date());
  const [conflicts, setConflicts] = useState({}); // id → {total, days} | {unavailable:true}
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tech: "", category: "pto", start_date: "", end_date: "", hours: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t, e] = await Promise.all([
        listTimeOff({ page_size: 500 }),
        listTechnicianProfiles().catch(() => ({ data: [] })),
        listEmployees().catch(() => ({ data: {} })),
      ]);
      setRows(r.data?.results || (Array.isArray(r.data) ? r.data : []));
      setTechs(Array.isArray(t.data) ? t.data : t.data?.results || []);
      setEmployees(e.data?.employees || []);
    } catch { toast.error("Couldn’t load time off."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const techInfo = useMemo(() => new Map(techs.map((t) => [Number(t.id), { user_id: techUserId(t), name: t.name || t.full_name || t.user_name || t.display_name || `Tech #${t.id}` }])), [techs]);
  const empByUser = useMemo(() => new Map(employees.map((e) => [e.user_id, e])), [employees]);
  const who = (r) => techInfo.get(Number(r.tech))?.name || r.tech_name || (r.tech ? `Tech #${r.tech}` : "—");
  const userOf = (r) => r.tech_user_id || techInfo.get(Number(r.tech))?.user_id || null;

  // conflict counts for pending requests (lazy, once)
  const pending = useMemo(() => rows.filter((r) => r.status === "pending"), [rows]);
  useEffect(() => {
    if (!isManager) return;
    pending.forEach((r) => {
      if (conflicts[r.id]) return;
      setConflicts((c) => ({ ...c, [r.id]: { loading: true } }));
      getTimeOffConflicts(r.id)
        .then((res) => setConflicts((c) => ({ ...c, [r.id]: res.data })))
        .catch((err) => setConflicts((c) => ({ ...c, [r.id]: { unavailable: true, status: err?.status } })));
    });
  }, [pending, isManager]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── calendar window ── */
  const days = useMemo(() => {
    const start = view === "week" ? startOfWeek(anchor) : startOfMonth(anchor);
    const n = view === "week" ? 7 : new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
    return Array.from({ length: n }, (_, i) => new Date(start.getTime() + i * DAY));
  }, [view, anchor]);
  const windowStart = iso(days[0]); const windowEnd = iso(days[days.length - 1]);
  const inWindow = (r) => r.start_date <= windowEnd && r.end_date >= windowStart && (r.status === "approved" || r.status === "pending");
  const people = useMemo(() => {
    const m = new Map();
    rows.filter(inWindow).forEach((r) => { const k = Number(r.tech); if (!m.has(k)) m.set(k, { tech: k, name: who(r), user_id: userOf(r), rows: [] }); m.get(k).rows.push(r); });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, windowStart, windowEnd]); // eslint-disable-line react-hooks/exhaustive-deps
  const offOn = (dayIso) => rows.filter((r) => r.status === "approved" && r.start_date <= dayIso && r.end_date >= dayIso).length;
  const shift = (dir) => setAnchor((a) => (view === "week" ? new Date(a.getTime() + dir * 7 * DAY) : new Date(a.getFullYear(), a.getMonth() + dir, 1)));
  const today = iso(new Date());

  /* ── balances ── per-employee allowance (days) from the employment record.
     No allowance = unknown, shown as "PTO not set", never as 0 / "0 days approved". */
  const balances = useMemo(() => techs.map((t) => {
    const uid = techUserId(t); const emp = uid ? empByUser.get(uid) : null;
    const mine = rows.filter((r) => Number(r.tech) === Number(t.id));
    return { tech: t.id, user_id: uid, name: t.name || t.full_name || t.user_name || t.display_name || `Tech #${t.id}`, role: emp?.role || "", hasRecord: !!emp, allowance: emp?.pto_allowance || null, pending: mine.filter((r) => r.status === "pending").length };
  }).sort((a, b) => a.name.localeCompare(b.name)), [techs, rows, empByUser]);
  const decided = useMemo(() => rows.filter((r) => (r.status === "approved" || r.status === "denied") && r.reviewed_at)
    .sort((a, b) => String(b.reviewed_at).localeCompare(String(a.reviewed_at))).slice(0, 12), [rows]);

  /* ── actions ── */
  const act = async (id, fn, label, verb) => {
    setBusyId(id);
    try {
      const note = (notes[id] || "").trim();
      await fn(id, note ? { note } : {});
      toast.success(label);
      setNotes((n) => { const c = { ...n }; delete c[id]; return c; });
      await load();
    } catch (e) { toast.error(timeOffErrorMessage(e, verb)); }
    finally { setBusyId(null); }
  };
  const isPast = (r) => (r.is_past != null ? !!r.is_past : String(r.end_date) < today);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) { toast.error("Pick a start and end date."); return; }
    if (isManager && !form.tech) { toast.error("Pick who this is for."); return; }
    setSubmitting(true);
    try {
      const body = { category: form.category, start_date: form.start_date, end_date: form.end_date, hours: form.hours === "" ? undefined : Number(form.hours), reason: form.reason || "" };
      if (isManager && form.tech) body.tech = Number(form.tech);
      const r = await createTimeOff(body);
      const filedFor = Number(r.data?.tech);
      if (isManager && form.tech && filedFor && filedFor !== Number(form.tech)) {
        // the backend that honours `tech` for managers isn't deployed: it filed this under the caller. Undo, say so.
        try { await cancelTimeOff(r.data.id); } catch { /* best effort */ }
        toast.error("On-behalf requests aren’t live on the backend yet — it filed under you, so it was cancelled. Ask the employee to submit it for now.");
      } else {
        toast.success(isManager && form.tech ? `Filed for ${techInfo.get(Number(form.tech))?.name || "the employee"}` : "Request submitted");
        setShowForm(false); setForm({ tech: "", category: "pto", start_date: "", end_date: "", hours: "", reason: "" });
      }
      await load();
    } catch (ex) {
      const detail = ex?.payload?.detail || ex?.message || "";
      toast.error(/technician profile/i.test(detail) ? "That person isn’t on the schedule yet, so there’s no profile to file time off against." : detail || "Couldn’t submit the request.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="team-tab to">
      {/* ── header: window + the one button ── */}
      <div className="to-head">
        <div className="to-window">
          <div className="to-seg">{["week", "month"].map((v) => <button key={v} type="button" className={view === v ? "is-on" : ""} onClick={() => setView(v)}>{v === "week" ? "This week" : "This month"}</button>)}</div>
          <button type="button" className="ops-btn secondary" onClick={() => shift(-1)}>‹</button>
          <span className="to-range">{view === "week" ? `${fmtDate(windowStart)} – ${fmtDate(windowEnd)}` : anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span>
          <button type="button" className="ops-btn secondary" onClick={() => shift(1)}>›</button>
          <button type="button" className="ops-btn secondary" onClick={() => setAnchor(new Date())}>Today</button>
        </div>
        <button type="button" className="ops-btn" onClick={() => setShowForm((s) => !s)}>{showForm ? "Close" : isManager ? "Request time off…" : "Request time off"}</button>
      </div>

      {showForm && (
        <section className="ppl-card ppl-card--wide">
          <h3>{isManager ? "Request time off for someone" : "Request time off"}</h3>
          <form className="ppl-form" onSubmit={submit}>
            {isManager && (
              <label><span>For</span>
                <select value={form.tech} onChange={(e) => setForm({ ...form, tech: e.target.value })}>
                  <option value="">Select…</option>
                  {techs.map((t) => <option key={t.id} value={t.id}>{techInfo.get(Number(t.id))?.name}</option>)}
                </select>
              </label>
            )}
            <label><span>Type</span><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</select></label>
            <label><span>Start</span><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></label>
            <label><span>End</span><input type="date" value={form.end_date} min={form.start_date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></label>
            <label><span>Hours <em>optional</em></span><input type="number" min="0" step="0.5" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} /></label>
            <label><span>Reason <em>optional</em></span><input type="text" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></label>
            <div className="ppl-form--wide ppl-actions"><button type="submit" className="ops-btn" disabled={submitting}>{submitting ? "Submitting…" : "Submit"}</button>{isManager && <span className="ppl-muted">Filed as pending under that person; approve it below.</span>}</div>
          </form>
        </section>
      )}

      {/* ── calendar: who is off, when ── */}
      <section className="ppl-card ppl-card--wide">
        <div className="ppl-row-head"><h3>Who’s off</h3><span className="ppl-muted">Approved is solid; pending is outlined. An approved day blocks the tech on the dispatch board.</span></div>
        {loading ? <div className="ppl-muted">Loading…</div> : (
          <div className="to-cal-wrap">
            <table className={`to-cal ${view}`}>
              <thead><tr><th className="to-name">Person</th>{days.map((d) => { const k = iso(d); const n = offOn(k); return <th key={k} className={`${k === today ? "is-today" : ""} ${d.getDay() === 0 || d.getDay() === 6 ? "is-wknd" : ""}`}><div>{view === "week" ? d.toLocaleDateString(undefined, { weekday: "short" }) : d.getDate()}</div><div className="to-th-sub">{view === "week" ? d.getDate() : ""}{n > 0 && <span className="to-count" title={`${n} off`}>{n}</span>}</div></th>; })}</tr></thead>
              <tbody>
                {people.length === 0 ? <tr><td className="to-name" colSpan={days.length + 1}><span className="ppl-muted">Nobody is off in this window.</span></td></tr>
                  : people.map((p) => (
                    <tr key={p.tech}>
                      <td className="to-name">{p.user_id ? <button type="button" className="ppl-link" onClick={() => openEmployee(p.user_id, "vacation")}>{p.name}</button> : p.name}</td>
                      {days.map((d) => { const k = iso(d); const r = p.rows.find((x) => x.start_date <= k && x.end_date >= k); return <td key={k} className={`to-cell ${r ? `is-${r.status}` : ""} ${k === today ? "is-today" : ""}`} title={r ? `${r.status} · ${String(r.category).replace(/_/g, " ")}${r.reason ? ` · ${r.reason}` : ""}` : ""}>{r ? <span className="to-chip">{view === "week" ? String(r.category).replace(/_/g, " ") : ""}</span> : null}</td>; })}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="ppl-grid">
        {/* ── pending queue with conflicts ── */}
        <section className="ppl-card ppl-card--wide">
          <div className="ppl-row-head"><h3>Waiting for approval{pending.length ? ` · ${pending.length}` : ""}</h3></div>
          {loading ? <div className="ppl-muted">Loading…</div>
            : pending.length === 0 ? <div className="ppl-muted">Nothing waiting.</div>
            : <div className="ppl-list">{pending.map((r) => {
              const c = conflicts[r.id];
              return (
                <div key={r.id} className="ppl-list-row">
                  <div className="ppl-list-main">
                    <span className="ppl-name">{userOf(r) ? <button type="button" className="ppl-link ppl-name" onClick={() => openEmployee(userOf(r), "vacation")}>{who(r)}</button> : who(r)}<span className="ppl-sub"> · {String(r.category || "").replace(/_/g, " ")}</span>{isPast(r) && <span className="ppl-pill to-past" title="These dates have passed. Approving records it and counts it against PTO; the board block has no scheduling effect.">Past</span>}</span>
                    <span className="ppl-sub">{fmtDate(r.start_date)} → {fmtDate(r.end_date)} · {daysBetween(r.start_date, r.end_date)} day{daysBetween(r.start_date, r.end_date) === 1 ? "" : "s"}{r.hours ? ` · ${r.hours} h` : ""}{r.reason ? ` · ${r.reason}` : ""}</span>
                    {isManager && !isPast(r) && (
                      c?.loading ? <span className="ppl-sub">checking the schedule…</span>
                        : c?.unavailable ? <span className="ppl-sub">schedule check not available{c.status === 404 ? " until the backend deploys" : ""}</span>
                        : c && c.total > 0 ? <span className="to-conflict">Has {c.total} scheduled stop{c.total === 1 ? "" : "s"} in that window · {c.days.map((d) => `${fmtDate(d.date)}: ${d.stops}`).join(", ")} — approving frees them for re-dispatch</span>
                        : c ? <span className="to-clear">No CRM stops scheduled in that window</span> : null
                    )}
                  </div>
                  {isManager ? (
                    <span className="ppl-row-actions">
                      <input type="text" className="to-note" maxLength={280} placeholder="Note (optional)" aria-label={`Note for ${who(r)}`} value={notes[r.id] || ""} onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))} />
                      <button type="button" className="ops-btn" disabled={busyId === r.id} onClick={() => act(r.id, approveTimeOff, isPast(r) ? "Approved — recorded (dates already passed)" : "Approved — blocked on the board", "approve")}>Approve</button>
                      <button type="button" className="ops-btn secondary" disabled={busyId === r.id} onClick={() => act(r.id, denyTimeOff, "Denied", "deny")}>Deny</button>
                    </span>
                  ) : <button type="button" className="ops-btn secondary" disabled={busyId === r.id} onClick={() => act(r.id, cancelTimeOff, "Cancelled", "cancel")}>Cancel</button>}
                </div>
              );
            })}</div>}
        </section>

        {/* ── decided: who approved/denied, when, and the note ── */}
        <section className="ppl-card ppl-card--wide">
          <div className="ppl-row-head"><h3>Decided</h3><span className="ppl-muted">Most recent first</span></div>
          {loading ? <div className="ppl-muted">Loading…</div>
            : decided.length === 0 ? <div className="ppl-muted">No requests approved or denied yet.</div>
            : <div className="ppl-list">{decided.map((r) => (
              <div key={r.id} className="ppl-list-row">
                <div className="ppl-list-main">
                  <span className="ppl-name">{who(r)}<span className="ppl-sub"> · {String(r.category || "").replace(/_/g, " ")} · {fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span></span>
                  <span className="ppl-sub">{r.status === "approved" ? "Approved" : "Denied"} by {r.reviewed_by_name || "unknown"} on {new Date(r.reviewed_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}{r.review_note ? ` · “${r.review_note}”` : ""}</span>
                </div>
                <span className={`ppl-pill ${r.status}`}>{r.status}</span>
              </div>
            ))}</div>}
        </section>

        {/* ── balances ── */}
        <section className="ppl-card ppl-card--wide">
          <h3>Balances</h3>
          <div className="ppl-muted" style={{ marginBottom: 6 }}>PTO days left this PTO year, from each person’s allowance on their employment record. Approved past dates count.</div>
          {balances.length === 0 ? <div className="ppl-muted">No technician profiles yet.</div> : (
            <div className="ppl-list to-balances">
              {balances.map((b) => (
                <div key={b.tech} className="ppl-list-row">
                  <div className="ppl-list-main">
                    <span className="ppl-name">{b.user_id ? <button type="button" className="ppl-link ppl-name" onClick={() => openEmployee(b.user_id, "vacation")}>{b.name}</button> : b.name}</span>
                    <span className="ppl-sub">{b.allowance
                      ? `${b.allowance.used_days} day${b.allowance.used_days === 1 ? "" : "s"} approved of ${b.allowance.days_per_year}${b.allowance.carry_in_days ? ` + ${b.allowance.carry_in_days} carried` : ""}${b.pending ? ` · ${b.pending} pending` : ""}`
                      : b.pending ? `${b.pending} pending` : ""}</span>
                  </div>
                  {b.allowance
                    ? <span className={`ppl-pill ${b.allowance.days_left <= 0 ? "denied" : "approved"}`}>{`${b.allowance.days_left} day${b.allowance.days_left === 1 ? "" : "s"} left`}</span>
                    : !b.hasRecord ? <span className="ppl-pill" title="No employment record for this person, so there is nowhere to set PTO yet.">no employment record</span>
                    : <button type="button" className="ppl-link to-pto-unset" onClick={() => b.user_id && openEmployee(b.user_id, "vacation")}>PTO not set — set it</button>}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── policy: honest ── */}
        <section className="ppl-card ppl-card--wide">
          <h3>Policy</h3>
          <div className="ppl-list">
            <div className="ppl-list-row"><div className="ppl-list-main"><span>Max people off per day</span><span className="ppl-sub">by role</span></div><span className="ppl-pill">not configured</span></div>
            <div className="ppl-list-row"><div className="ppl-list-main"><span>Blackout dates</span><span className="ppl-sub">no time off approved on these days</span></div><span className="ppl-pill">not configured</span></div>
            <div className="ppl-list-row"><div className="ppl-list-main"><span>Accrual</span><span className="ppl-sub">per person — days per year, annual grant or monthly, carryover, year start</span></div><span className="ppl-pill">on each employee’s record</span></div>
          </div>
          <div className="ppl-muted" style={{ marginTop: 8 }}>Caps and blackout dates have no backend yet, so nothing here is enforced. Each person’s PTO allowance is set on their employee card → Time Off.</div>
        </section>
      </div>
    </div>
  );
}
